import React, { Component } from "react";

import { setInTransition } from "./Collapsible.helpers";

type CollapsibleProps = {
  transitionTime?: number;
  transitionCloseTime?: number | null;
  triggerTagName?: string;
  easing?: string;
  open?: boolean;
  classParentString?: string;
  triggerDisabled?: boolean;
  lazyRender?: boolean;
  overflowWhenOpen?: string;
  contentHiddenWhenClosed?: boolean;
  openedClassName?: string;
  triggerWhenOpen?: boolean;
  trigger?: any;
  triggerStyle?: React.CSSProperties | null;
  triggerClassName?: string;
  triggerOpenedClassName?: string;
  contentOuterClassName?: string;
  contentInnerClassName?: string;
  className?: string;
  triggerSibling?: React.ReactNode | (() => React.ReactNode);
  onOpen?: () => void;
  onClose?: () => void;
  onOpening?: () => void;
  onClosing?: () => void;
  onTriggerOpening?: () => void;
  onTriggerClosing?: () => void;
  tabIndex?: number | null;
  contentContainerTagName?: keyof any;
  children?: React.ReactNode;
  handleTriggerClick?: (accordionPosition?: number) => void;
  accordionPosition?: number;
  containerElementProps?: React.HTMLAttributes<HTMLDivElement>;
  triggerElementProps?: React.HTMLAttributes<HTMLElement>;
};

type CollapsibleState = {
  isClosed: boolean;
  shouldSwitchAutoOnNextCycle: boolean;
  height: string | number;
  transition: string;
  hasBeenOpened: boolean;
  overflow: string;
  inTransition: boolean;
  shouldOpenOnNextCycle?: boolean;
};

class Collapsible extends Component<CollapsibleProps, CollapsibleState> {
  static defaultProps: CollapsibleProps = {
    transitionTime: 400,
    transitionCloseTime: null,
    triggerTagName: "span",
    easing: "linear",
    open: false,
    classParentString: "Collapsible",
    triggerDisabled: false,
    lazyRender: false,
    overflowWhenOpen: "hidden",
    contentHiddenWhenClosed: false,
    openedClassName: "",
    triggerStyle: null,
    triggerClassName: "",
    triggerOpenedClassName: "",
    contentOuterClassName: "",
    contentInnerClassName: "",
    className: "",
    triggerSibling: null,
    onOpen: () => {},
    onClose: () => {},
    onOpening: () => {},
    onClosing: () => {},
    onTriggerOpening: () => {},
    onTriggerClosing: () => {},
    tabIndex: null,
    contentContainerTagName: "div",
  };

  private timeout: number | undefined;
  private innerRef: HTMLDivElement | null = null;

  constructor(props: CollapsibleProps) {
    super(props);

    this.timeout = undefined;

    if (props.open) {
      this.state = {
        isClosed: false,
        shouldSwitchAutoOnNextCycle: false,
        height: "auto",
        transition: "none",
        hasBeenOpened: true,
        overflow: props.overflowWhenOpen || "hidden",
        inTransition: false,
      };
    } else {
      this.state = {
        isClosed: true,
        shouldSwitchAutoOnNextCycle: false,
        height: 0,
        transition: `height ${props.transitionTime}ms ${props.easing}`,
        hasBeenOpened: false,
        overflow: "hidden",
        inTransition: false,
      };
    }
  }

  componentDidUpdate(prevProps: CollapsibleProps, prevState: CollapsibleState) {
    if (this.state.shouldOpenOnNextCycle) {
      this.continueOpenCollapsible();
    }

    if (
      (prevState.height === "auto" || prevState.height === 0) &&
      this.state.shouldSwitchAutoOnNextCycle
    ) {
      window.clearTimeout(this.timeout);
      this.timeout = window.setTimeout(() => {
        this.setState({
          height: 0,
          overflow: "hidden",
          isClosed: true,
          shouldSwitchAutoOnNextCycle: false,
        });
      }, 50);
    }

    if (prevProps.open !== this.props.open) {
      if (this.props.open) {
        this.openCollapsible();
        this.props.onOpening?.();
      } else {
        this.closeCollapsible();
        this.props.onClosing?.();
      }
    }
  }

  componentWillUnmount() {
    window.clearTimeout(this.timeout);
  }

  closeCollapsible() {
    if (this.innerRef) {
      this.setState({
        shouldSwitchAutoOnNextCycle: true,
        height: this.innerRef.scrollHeight,
        transition: `height ${
          this.props.transitionCloseTime || this.props.transitionTime
        }ms ${this.props.easing}`,
        inTransition: setInTransition(this.innerRef.scrollHeight),
      });
    }
  }

  openCollapsible() {
    if (this.innerRef) {
      this.setState({
        inTransition: setInTransition(this.innerRef.scrollHeight),
        shouldOpenOnNextCycle: true,
      });
    }
  }

  continueOpenCollapsible = () => {
    if (this.innerRef) {
      this.setState({
        height: this.innerRef.scrollHeight,
        transition: `height ${this.props.transitionTime}ms ${this.props.easing}`,
        isClosed: false,
        hasBeenOpened: true,
        inTransition: setInTransition(this.innerRef.scrollHeight),
        shouldOpenOnNextCycle: false,
      });
    }
  };

  handleTriggerClick = (event: React.MouseEvent) => {
    if (this.props.triggerDisabled || this.state.inTransition) {
      return;
    }

    event.preventDefault();

    if (this.props.handleTriggerClick) {
      this.props.handleTriggerClick(this.props.accordionPosition);
    } else {
      if (this.state.isClosed) {
        this.openCollapsible();
        this.props.onOpening?.();
        this.props.onTriggerOpening?.();
      } else {
        this.closeCollapsible();
        this.props.onClosing?.();
        this.props.onTriggerClosing?.();
      }
    }
  };

  renderNonClickableTriggerElement() {
    if (typeof this.props.triggerSibling === "string") {
      return (
        <span className={`${this.props.classParentString}__trigger-sibling`}>
          {this.props.triggerSibling}
        </span>
      );
    } else if (typeof this.props.triggerSibling === "function") {
      return this.props.triggerSibling();
    } else if (this.props.triggerSibling) {
      return <>{this.props.triggerSibling}</>;
    }
    return null;
  }

  handleTransitionEnd = (e: React.TransitionEvent) => {
    if (e.target !== this.innerRef) {
      return;
    }
    if (!this.state.isClosed) {
      this.setState({
        height: "auto",
        overflow: this.props.overflowWhenOpen || "hidden",
        inTransition: false,
      });
      this.props.onOpen?.();
    } else {
      this.setState({ inTransition: false });
      this.props.onClose?.();
    }
  };

  setInnerRef = (ref: HTMLDivElement | null) => {
    this.innerRef = ref;
  };

  render() {
    const dropdownStyle: React.CSSProperties = {
      height: this.state.height,
      WebkitTransition: this.state.transition,
      msTransition: this.state.transition,
      transition: this.state.transition,
      overflow: this.state.overflow,
    };

    const openClass = this.state.isClosed ? "is-closed" : "is-open";
    const disabledClass = this.props.triggerDisabled ? "is-disabled" : "";

    const trigger =
      !this.state.isClosed && this.props.triggerWhenOpen !== undefined
        ? this.props.triggerWhenOpen
        : this.props.trigger;

    const ContentContainerElement: any =
      this.props.contentContainerTagName || "div";
    const TriggerElement: any = this.props.triggerTagName || "span";

    const children =
      this.props.lazyRender &&
      !this.state.hasBeenOpened &&
      this.state.isClosed &&
      !this.state.inTransition
        ? null
        : this.props.children;

    const { classParentString, contentOuterClassName, contentInnerClassName } =
      this.props;

    const triggerClassString = `${classParentString}__trigger ${openClass} ${disabledClass} ${
      this.state.isClosed
        ? this.props.triggerClassName
        : this.props.triggerOpenedClassName
    }`;

    const parentClassString = `${classParentString} ${
      this.state.isClosed ? this.props.className : this.props.openedClassName
    }`;

    const outerClassString = `${classParentString}__contentOuter ${contentOuterClassName}`;
    const innerClassString = `${classParentString}__contentInner ${contentInnerClassName}`;

    return (
      <ContentContainerElement
        className={parentClassString.trim()}
        {...this.props.containerElementProps}
      >
        <TriggerElement
          className={triggerClassString.trim()}
          onClick={this.handleTriggerClick}
          style={this.props.triggerStyle || undefined}
          tabIndex={this.props.tabIndex || undefined}
          {...this.props.triggerElementProps}
        >
          {trigger}
        </TriggerElement>

        {this.renderNonClickableTriggerElement()}

        <div
          className={outerClassString.trim()}
          style={dropdownStyle}
          onTransitionEnd={this.handleTransitionEnd}
          ref={this.setInnerRef}
          hidden={
            this.props.contentHiddenWhenClosed &&
            this.state.isClosed &&
            !this.state.inTransition
          }
        >
          <div className={innerClassString.trim()}>{children}</div>
        </div>
      </ContentContainerElement>
    );
  }
}

export default Collapsible;
